"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "ProgressReporter", {
    enumerable: true,
    get: function() {
        return ProgressReporter;
    }
});
const _events = /*#__PURE__*/ _interop_require_default(require("events"));
const _logger = require("@lage-run/logger");
const _taskreporter = require("@ms-cloudpack/task-reporter");
const _gradientstring = /*#__PURE__*/ _interop_require_default(require("gradient-string"));
const _chalk = /*#__PURE__*/ _interop_require_default(require("chalk"));
const _formathrtime = require("@lage-run/format-hrtime");
const _formatBytes = require("./formatBytes.js");
const _slowestTargetRuns = require("./slowestTargetRuns.js");
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
const colors = {
    [_logger.LogLevel.info]: _chalk.default.white,
    [_logger.LogLevel.verbose]: _chalk.default.gray,
    [_logger.LogLevel.warn]: _chalk.default.white,
    [_logger.LogLevel.error]: _chalk.default.hex("#FF1010"),
    [_logger.LogLevel.silly]: _chalk.default.green,
    task: _chalk.default.hex("#00DDDD"),
    pkg: _chalk.default.hex("#FFD66B"),
    ok: _chalk.default.green,
    error: _chalk.default.red,
    warn: _chalk.default.yellow
};
function fancy(str) {
    return (0, _gradientstring.default)({
        r: 237,
        g: 178,
        b: 77
    }, "cyan")(str);
}
class ProgressReporter {
    createTaskReporter() {
        return new _taskreporter.TaskReporter({
            productName: "lage",
            version: this.options.version,
            showCompleted: true,
            showConsoleDebug: true,
            showConsoleError: true,
            showConsoleInfo: true,
            showConsoleLog: true,
            showConsoleWarn: true,
            showErrors: true,
            showPending: true,
            showStarted: true,
            showSummary: true,
            showTaskDetails: true,
            showTaskExtended: true
        });
    }
    log(entry) {
        // save the logs for errors
        if (entry.data?.target?.id) {
            if (!this.logEntries.has(entry.data.target.id)) {
                this.logEntries.set(entry.data.target.id, []);
            }
            this.logEntries.get(entry.data.target.id).push(entry);
        }
        // if "hidden", do not even attempt to record or report the entry
        if (entry?.data?.target?.hidden) {
            return;
        }
        if (entry.data && entry.data.schedulerRun) {
            this.startTime = entry.data.schedulerRun.startTime;
        }
        if (entry.data && entry.data.status && entry.data.target) {
            const target = entry.data.target;
            const status = entry.data.status;
            const reporterTask = this.tasks.has(target.id) ? this.tasks.get(target.id) : this.taskReporter.addTask(target.label, true);
            if (reporterTask) {
                this.tasks.set(target.id, reporterTask);
                switch(status){
                    case "running":
                        reporterTask.start();
                        break;
                    case "success":
                        reporterTask.complete({
                            status: "complete"
                        });
                        break;
                    case "aborted":
                        reporterTask.complete({
                            status: "abort"
                        });
                        break;
                    case "skipped":
                        reporterTask.complete({
                            status: "skip"
                        });
                        break;
                    case "failed":
                        reporterTask.complete({
                            status: "fail"
                        });
                        break;
                }
            }
        }
    }
    print(message) {
        this.logStream.write(message + "\n");
    }
    hr() {
        this.print("┈".repeat(80));
    }
    summarize(schedulerRunSummary) {
        const { targetRuns, targetRunByStatus, duration } = schedulerRunSummary;
        const { failed, aborted, skipped, success, pending, running, queued } = targetRunByStatus;
        // If we are printing summary, and there are still some running / queued tasks - report them as aborted
        for (const wrappedTarget of running.concat(queued)){
            const reporterTask = this.tasks.get(wrappedTarget);
            if (reporterTask) {
                reporterTask.complete({
                    status: "abort"
                });
            }
        }
        const statusColorFn = {
            success: _chalk.default.greenBright,
            failed: _chalk.default.redBright,
            skipped: _chalk.default.gray,
            running: _chalk.default.yellow,
            pending: _chalk.default.gray,
            aborted: _chalk.default.red,
            queued: _chalk.default.magenta
        };
        if (targetRuns.size > 0) {
            this.print(_chalk.default.cyanBright(`\nSummary`));
            this.hr();
            const slowestTargets = (0, _slowestTargetRuns.slowestTargetRuns)([
                ...targetRuns.values()
            ]);
            for (const wrappedTarget of slowestTargets){
                if (wrappedTarget.target.hidden) {
                    continue;
                }
                const colorFn = statusColorFn[wrappedTarget.status] ?? _chalk.default.white;
                const target = wrappedTarget.target;
                const hasDurations = !!wrappedTarget.duration && !!wrappedTarget.queueTime;
                const queueDuration = hasDurations ? (0, _formathrtime.hrtimeDiff)(wrappedTarget.queueTime, wrappedTarget.startTime) : [
                    0,
                    0
                ];
                if (wrappedTarget.status === "running") {
                    const reporterTask = this.tasks.get(wrappedTarget.target.id);
                    if (reporterTask) {
                        reporterTask.complete({
                            status: "fail"
                        });
                    }
                }
                this.print(`${target.label} ${colorFn(`${wrappedTarget.status === "running" ? "running - incomplete" : wrappedTarget.status}${hasDurations ? `, took ${(0, _formathrtime.formatDuration)((0, _formathrtime.hrToSeconds)(wrappedTarget.duration))}, queued for ${(0, _formathrtime.formatDuration)((0, _formathrtime.hrToSeconds)(queueDuration))}` : ""}`)}`);
            }
            this.print(`success: ${success.length}, skipped: ${skipped.length}, pending: ${pending.length}, aborted: ${aborted.length}, failed: ${failed.length}`);
            this.print(`worker restarts: ${schedulerRunSummary.workerRestarts}, max worker memory usage: ${(0, _formatBytes.formatBytes)(schedulerRunSummary.maxWorkerMemoryUsage)}`);
        } else {
            this.print("Nothing has been run.");
        }
        this.hr();
        if (failed && failed.length > 0) {
            for (const targetId of failed){
                const target = targetRuns.get(targetId)?.target;
                if (target) {
                    const { packageName, task } = target;
                    const failureLogs = this.logEntries.get(targetId);
                    this.print(`[${colors.pkg(packageName ?? "<root>")} ${colors.task(task)}] ${colors[_logger.LogLevel.error]("ERROR DETECTED")}`);
                    if (failureLogs) {
                        for (const entry of failureLogs){
                            // Log each entry separately to prevent truncation
                            this.print(entry.msg);
                        }
                    }
                    this.hr();
                }
            }
        }
        const allCacheHits = [
            ...targetRuns.values()
        ].filter((run)=>!run.target.hidden).length === skipped.length;
        const allCacheHitText = allCacheHits ? fancy(`All targets skipped!`) : "";
        this.print(`Took a total of ${(0, _formathrtime.formatDuration)((0, _formathrtime.hrToSeconds)(duration))} to complete. ${allCacheHitText}`);
    }
    constructor(options = {
        concurrency: 0,
        version: "0.0.0"
    }){
        _define_property(this, "options", void 0);
        _define_property(this, "logStream", void 0);
        _define_property(this, "startTime", void 0);
        _define_property(this, "logEvent", void 0);
        _define_property(this, "logEntries", void 0);
        _define_property(this, "taskReporter", void 0);
        _define_property(this, "tasks", void 0);
        this.options = options;
        this.logStream = process.stdout;
        this.startTime = [
            0,
            0
        ];
        this.logEvent = new _events.default();
        this.logEntries = new Map();
        this.tasks = new Map();
        this.taskReporter = this.createTaskReporter();
        this.print(`${fancy("lage")} - Version ${options.version} - ${options.concurrency} Workers`);
    }
}
